import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'

interface SystemStats {
  totalEvents: number
  totalSessions: number
  oldestEvent: string | null
  eventsPerMinute: number
  tableSizes: { name: string; size: string }[]
}

export default function AdminSystemPage() {
  const [stats, setStats] = useState<SystemStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [warnings, setWarnings] = useState<string[]>([])

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    setIsLoading(true)
    const warningsList: string[] = []

    try {
      // Total events
      const { count: eventsCount } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })

      // Total sessions
      const { count: sessionsCount } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true })

      // Oldest event
      const { data: oldestEvent } = await supabase
        .from('events')
        .select('timestamp')
        .order('timestamp', { ascending: true })
        .limit(1)
        .single() as { data: { timestamp: string } | null }

      // Check if oldest event is too old (> 35 days)
      if (oldestEvent?.timestamp) {
        const daysSinceOldest = Math.floor(
          (Date.now() - new Date(oldestEvent.timestamp).getTime()) / (1000 * 60 * 60 * 24)
        )
        if (daysSinceOldest > 35) {
          warningsList.push(`Cleanup job may have failed - oldest event is ${daysSinceOldest} days old`)
        }
      }

      // Events in last hour
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString()
      const { count: eventsLastHour } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .gte('timestamp', oneHourAgo)

      const eventsPerMinute = Math.round((eventsLastHour || 0) / 60)

      // Estimate table sizes (using row counts as proxy since we can't get actual sizes)
      const tableSizes = [
        { name: 'events', size: `~${eventsCount?.toLocaleString() || 0} rows` },
        { name: 'sessions', size: `~${sessionsCount?.toLocaleString() || 0} rows` },
      ]

      // Get counts for other tables
      const { count: profilesCount } = await supabase.from('profiles').select('*', { count: 'exact', head: true })
      const { count: sitesCount } = await supabase.from('sites').select('*', { count: 'exact', head: true })
      const { count: dailyStatsCount } = await supabase.from('daily_stats').select('*', { count: 'exact', head: true })

      tableSizes.push(
        { name: 'profiles', size: `~${profilesCount?.toLocaleString() || 0} rows` },
        { name: 'sites', size: `~${sitesCount?.toLocaleString() || 0} rows` },
        { name: 'daily_stats', size: `~${dailyStatsCount?.toLocaleString() || 0} rows` }
      )

      // Warning if events count is high
      if ((eventsCount || 0) > 400000) {
        warningsList.push('Event count is getting high - consider reviewing data retention')
      }

      setStats({
        totalEvents: eventsCount || 0,
        totalSessions: sessionsCount || 0,
        oldestEvent: oldestEvent?.timestamp || null,
        eventsPerMinute,
        tableSizes,
      })

      setWarnings(warningsList)
    } catch (error) {
      console.error('Failed to fetch system stats:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">System Health</h1>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-2 mb-6">
          {warnings.map((warning, i) => (
            <div
              key={i}
              className="bg-yellow-900/30 border border-yellow-500/50 rounded-xl p-4"
            >
              <p className="text-yellow-300 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {warning}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">Total Events</p>
          <p className="text-2xl font-bold text-white">{stats?.totalEvents.toLocaleString()}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">Total Sessions</p>
          <p className="text-2xl font-bold text-white">{stats?.totalSessions.toLocaleString()}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">Events/min (last hour)</p>
          <p className="text-2xl font-bold text-white">{stats?.eventsPerMinute}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">Oldest Event</p>
          <p className="text-xl font-bold text-white">
            {stats?.oldestEvent
              ? format(new Date(stats.oldestEvent), 'MMM d, yyyy')
              : 'N/A'}
          </p>
        </div>
      </div>

      {/* Table sizes */}
      <div className="bg-gray-800 rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-white mb-4">Table Sizes</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="pb-3 font-medium">Table</th>
              <th className="pb-3 font-medium text-right">Size</th>
            </tr>
          </thead>
          <tbody>
            {stats?.tableSizes.map((table) => (
              <tr key={table.name} className="border-b border-gray-700 last:border-0">
                <td className="py-3 text-white font-mono">{table.name}</td>
                <td className="py-3 text-gray-300 text-right">{table.size}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Data retention info */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Data Retention</h3>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-400">Retention Period</dt>
            <dd className="text-white">30 days</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-400">Cleanup Schedule</dt>
            <dd className="text-white">Daily at midnight UTC</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-400">Oldest Event</dt>
            <dd className="text-white">
              {stats?.oldestEvent
                ? format(new Date(stats.oldestEvent), 'PPpp')
                : 'No events'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}
